/**
 * Three reconciliation lines a player can check without leaving the page (QA findings R7): the yearly headlines to the finale total, the year's and the game's payouts split into dividends and buybacks, and each answered card's
 * priced value to the value counted, with the reason for the gap. Pure; every figure is one the report already carries, printed the way the page prints it, and every sum is of printed parts.
 */
import type { ContentCatalog, RoundReport } from "@bsim/contracts";
import { fiscalYearLabel, formatSharesM, formatUsdM, formatUsdPerShare } from "../format";
import { printedParts } from "../score/labels";
import { cardName } from "./surprise";
import { answeredBeforeSigning, perCardLegs } from "./cardEffect";
import { ARRIVED_BASIS, PATH_BASIS, scoreLegAmount, scoreLegs } from "./decomposition";

export const ANSWERS_BASIS = "what your answers added against leaving each card to its usual outcome, counted on the year as it ran";

const money = (v: number): string => formatUsdPerShare(v, { signed: true });

export interface YearReports { readonly roundNo: number; readonly report: RoundReport }

/**
 * (a) "FY2026 −$3.08, FY2027 −$3.04, FY2028 −$0.90, FY2029 +$3.97. Each is your score to date, so the finale is the last one, not their sum. It is price +$x and cash returned +$y and look-through +$z, which add to it."
 * Null with fewer than two years (nothing to bridge) or when the last year carries no split.
 */
export function headlinesToFinale(years: readonly YearReports[], catalog: ContentCatalog): string[] | null {
  const ordered = [...years].sort((a, b) => a.roundNo - b.roundNo);
  const last = ordered.at(-1);
  if (ordered.length < 2 || last === undefined) return null;
  const heads = ordered.map((y) => `${fiscalYearLabel(catalog, y.roundNo)} ${money(y.report.score.valueCreatedUsdPerShare)}`);
  const lines = [`${heads.join(", ")}. Each is your score to date, not that year's alone, so the finale is the last of them, not their sum.`];
  const c = last.report.score.components;
  if (c !== undefined) {
    const p = printedParts(c, last.report.score.valueCreatedUsdPerShare);
    const sh = c.shares;
    const shares = sh === undefined ? "" : ` Cash returned counts the shares each bank retired since the start: you ${formatSharesM(sh.simStartShares - sh.youShares)}, your twin ${formatSharesM(sh.simStartShares - sh.twinShares)}.`;
    lines.push(`${fiscalYearLabel(catalog, last.roundNo)} is share price ${money(p.price)} and cash returned ${money(p.cash)} and the look-through revaluation ${money(p.lookThrough)} (the official score less price and cash), which add to ${money(last.report.score.valueCreatedUsdPerShare)}.${shares}`);
  }
  return lines;
}

/** The sum is the sum of the printed parts, so the parts and the total on the page add exactly (the unrounded sum can print a tenth more). */
const split = (dividendsUsd: number, buybacksUsd: number): string => {
  const tenth = (v: number): number => Math.round(Number((v / 1e5).toPrecision(12)));
  return `dividends ${formatUsdM(dividendsUsd)} and buybacks ${formatUsdM(buybacksUsd)} (${formatUsdM((tenth(dividendsUsd) + tenth(buybacksUsd)) * 1e5)})`;
};

/**
 * (b) "In FY2029 you paid dividends $x and buybacks $y ($z); your twin paid ... Over the game: ... Buybacks were paused by the outlook signal in 2 of the year's 4 quarters." Dividends level with the twin's are said to be.
 * Null when the year carries no components (older results).
 */
export function payoutSplit(years: readonly YearReports[], current: RoundReport, catalog: ContentCatalog): string[] | null {
  const own = current.score.components?.roundEnd;
  if (own === undefined) return null;
  const twin = current.score.components?.twin;
  const fy = fiscalYearLabel(catalog, current.roundNo);
  const line = [`In ${fy} you paid ${split(own.dividendsUsd, own.buybacksUsd)}`];
  if (twin !== undefined) line.push(`; your twin paid ${split(twin.dividendsUsd, twin.buybacksUsd)}`);
  line.push(".");
  if (twin !== undefined && Math.abs(own.dividendsUsd - twin.dividendsUsd) < 5e5) line.push(" Your dividends equal your twin's, so they add nothing to the score against it.");
  const lines = [line.join("")];
  const ends = years.map((y) => y.report.score.components?.roundEnd);
  if (years.length > 1 && ends.every((e) => e !== undefined)) lines.push(`Over the game, ${split(ends.reduce((t, e) => t + (e?.dividendsUsd ?? 0), 0), ends.reduce((t, e) => t + (e?.buybacksUsd ?? 0), 0))}.`);
  const paused = current.outlook?.pausedBuybackMonths.length ?? 0;
  const whilePaused = current.outlook?.ownBuybacksWhilePausedUsd ?? 0;
  if (paused > 0) lines.push(`Buybacks are paused when unemployment is rising (a negative economic outlook). They were paused in ${paused} of the year's ${current.quarterly.length || 4} quarters${whilePaused > 0 ? `, and ${formatUsdM(whilePaused)} of your buybacks were still posted in those quarters` : ""}. A pause shown on the Outlook page applies to the quarters ahead, not these.`);
  return lines;
}

/**
 * (c) One line per answered card: "“Title”: priced +$7.36 when you answered, counted −$1.85 in your score (−$9.21 apart: the year ran differently from the forecast the card was priced on: rates, credit and any chance outcome after your answer)".
 * The reason is the one the report states: an answer given before signing is inside the signed figure and is not counted again; an answer with a credit-path leg names it; any other gap is the year as it ran. Empty with no priced answer.
 */
export function pricedToCounted(report: RoundReport): string[] {
  const legs = perCardLegs(report) ?? [];
  return report.events.flatMap((e) => {
    const priced = e.response?.pricedAtAnswer;
    const shown = priced === undefined ? undefined : priced.scoreUsdPerShare ?? priced.priceUsdPerShare;
    if (e.status !== "responded" || shown === undefined) return [];
    const title = cardName(e.card.title);
    if (answeredBeforeSigning(e)) return [`${title}: priced ${money(shown)} when you answered; you answered before you signed, so it was part of the plan you signed and is not counted again.`];
    const counted = legs.find((l) => l.eventInstanceId === e.eventInstanceId)?.scoreUsd ?? (e.response?.legs === undefined ? undefined : e.response.legs.reduce((t, leg) => t + leg.usdPerShare, 0));
    if (counted === undefined) return [];
    // The gap is the difference of the two printed prices (to the cent), so the line subtracts on the page.
    const gap = (Math.round(counted * 100) - Math.round(shown * 100)) / 100;
    if (gap === 0) return [`${title}: priced ${money(shown)} when you answered, counted ${money(counted)}: the year ran as priced.`];
    const credit = e.response?.legs?.some((l) => /credit path/.test(l.label)) === true;
    const why = credit ? "the year's actual credit path moved it" : "the year ran differently from the forecast the card was priced on: rates, credit and any chance outcome after your answer";
    return [`${title}: priced ${money(shown)} when you answered, counted ${money(counted)} in your score (${money(gap)} apart: ${why}).`];
  });
}

/** What the three legs are, and the year's own figures for them: "Market off the forecast −$1.09 is ..., cards that arrived +$0.66 is ..., your card answers +$0.93 is ...". Null without the split. */
export function splitDefinitions(report: RoundReport): string[] | null {
  const l = scoreLegs(report);
  const path = scoreLegAmount(l, "path"), arrived = scoreLegAmount(l, "cardsArrived"), answers = scoreLegAmount(l, "cardAnswers");
  if (path === undefined) return null;
  return [
    `Market off the forecast ${money(path)}: ${PATH_BASIS}.`,
    ...(arrived === undefined ? [] : [`Cards that arrived ${money(arrived)}: ${ARRIVED_BASIS}. A card's own page assumes you do nothing; this line is what the card actually did to your year, against your twin.`]),
    ...(answers === undefined ? [] : [`Your card answers ${money(answers)}: ${ANSWERS_BASIS}.`]),
  ];
}
